import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from '../config/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const CART_ACTIVITY_DEBOUNCE_MS = 2000;

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const cartActivityTimeoutRef = useRef(null);

  // Initialize cart from localStorage immediately to prevent reset on navigation
  const getInitialCart = () => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        // Validate that it's an array
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
    return [];
  };

  const [cart, setCart] = useState(getInitialCart);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  // Report cart activity to backend for push reminders (debounced)
  useEffect(() => {
    if (!user) return;
    const count = cart.reduce((total, rc) => total + rc.items.reduce((s, i) => s + i.quantity, 0), 0);
    if (cartActivityTimeoutRef.current) clearTimeout(cartActivityTimeoutRef.current);
    cartActivityTimeoutRef.current = setTimeout(() => {
      cartActivityTimeoutRef.current = null;
      axios.patch(`${API_URL}/users/me/cart-activity`, { cartItemCount: count }).catch(() => {});
    }, CART_ACTIVITY_DEBOUNCE_MS);
    return () => {
      if (cartActivityTimeoutRef.current) clearTimeout(cartActivityTimeoutRef.current);
    };
  }, [user, cart]);

  const addToCart = (restaurantId, restaurantName, item, type = 'restaurant') => {
    setCart((prevCart) => {
      // Allow multiple restaurants/stores in cart - no restriction

      // Find existing cart for this restaurant/store
      const existingCartIndex = prevCart.findIndex((c) => c.restaurantId === restaurantId);
      
      if (existingCartIndex !== -1) {
        // Restaurant/store cart exists
        const existingCart = prevCart[existingCartIndex];
        
        // Normalize variations for comparison (handle empty objects and key order)
        const normalizeVariations = (variations) => {
          if (!variations || Object.keys(variations).length === 0) {
            return null;
          }
          const sorted = Object.keys(variations).sort().reduce((acc, key) => {
            acc[key] = variations[key];
            return acc;
          }, {});
          return JSON.stringify(sorted);
        };
        const normalizeIngredients = (ingredients) => {
          if (!ingredients || !Array.isArray(ingredients) || ingredients.length === 0) {
            return null;
          }
          return JSON.stringify([...ingredients].sort());
        };
        
        const itemVariationsKey = normalizeVariations(item.selectedVariations);
        const itemIngredientsKey = normalizeIngredients(item.selectedIngredients);
        
        const itemIdStr = String(item._id);
        const existingItemIndex = existingCart.items.findIndex((i) => {
          const existingVariationsKey = normalizeVariations(i.selectedVariations);
          const existingIngredientsKey = normalizeIngredients(i.selectedIngredients);
          return String(i._id) === itemIdStr && existingVariationsKey === itemVariationsKey && existingIngredientsKey === itemIngredientsKey;
        });
        
        if (existingItemIndex !== -1) {
          // Item with same ID and variations exists - increment quantity
          const updatedCart = [...prevCart];
          updatedCart[existingCartIndex] = {
            ...existingCart,
            items: [...existingCart.items]
          };
          updatedCart[existingCartIndex].items[existingItemIndex] = {
            ...updatedCart[existingCartIndex].items[existingItemIndex],
            quantity: updatedCart[existingCartIndex].items[existingItemIndex].quantity + (item.quantity || 1)
          };
          return updatedCart;
        } else {
          // Different item (different ID or variations) - add as new item
          const updatedCart = [...prevCart];
          updatedCart[existingCartIndex] = {
            ...existingCart,
            items: [...existingCart.items, { ...item, quantity: item.quantity || 1 }]
          };
          return updatedCart;
        }
      } else {
        // New restaurant/store - create new cart entry
        return [
          ...prevCart,
          {
            restaurantId,
            restaurantName,
            type,
            items: [{ ...item, quantity: item.quantity || 1 }]
          }
        ];
      }
    });
  };

  const removeFromCart = (restaurantId, itemId, selectedVariations = null, selectedIngredients = null) => {
    setCart((prevCart) => {
      const restaurantCartIndex = prevCart.findIndex((c) => c.restaurantId === restaurantId);
      if (restaurantCartIndex !== -1) {
        const restaurantCart = prevCart[restaurantCartIndex];
        
        const normalizeVariations = (variations) => {
          if (!variations || Object.keys(variations).length === 0) return null;
          const sorted = Object.keys(variations).sort().reduce((acc, key) => {
            acc[key] = variations[key];
            return acc;
          }, {});
          return JSON.stringify(sorted);
        };
        const normalizeIngredients = (ingredients) => {
          if (!ingredients || !Array.isArray(ingredients) || ingredients.length === 0) return null;
          return JSON.stringify([...ingredients].sort());
        };
        
        const itemVariationsKey = normalizeVariations(selectedVariations);
        const itemIngredientsKey = normalizeIngredients(selectedIngredients);
        const itemIdStr = String(itemId);
        const filteredItems = restaurantCart.items.filter((i) => {
          if (selectedVariations !== null || selectedIngredients !== null) {
            const existingVariationsKey = normalizeVariations(i.selectedVariations);
            const existingIngredientsKey = normalizeIngredients(i.selectedIngredients);
            return !(String(i._id) === itemIdStr && existingVariationsKey === itemVariationsKey && existingIngredientsKey === itemIngredientsKey);
          }
          return String(i._id) !== itemIdStr;
        });
        
        // If no items left, remove the entire restaurant cart
        if (filteredItems.length === 0) {
          return prevCart.filter((c) => c.restaurantId !== restaurantId);
        }
        
        // Update the restaurant cart with filtered items
        const updatedCart = [...prevCart];
        updatedCart[restaurantCartIndex] = {
          ...restaurantCart,
          items: filteredItems
        };
        return updatedCart;
      }
      return prevCart;
    });
  };

  const updateQuantity = (restaurantId, itemId, quantity, selectedVariations = null, selectedIngredients = null) => {
    if (quantity <= 0) {
      removeFromCart(restaurantId, itemId, selectedVariations, selectedIngredients);
      return;
    }
    setCart((prevCart) => {
      const restaurantCartIndex = prevCart.findIndex((c) => c.restaurantId === restaurantId);
      if (restaurantCartIndex !== -1) {
        const restaurantCart = prevCart[restaurantCartIndex];
        
        const normalizeVariations = (variations) => {
          if (!variations || Object.keys(variations).length === 0) return null;
          const sorted = Object.keys(variations).sort().reduce((acc, key) => {
            acc[key] = variations[key];
            return acc;
          }, {});
          return JSON.stringify(sorted);
        };
        const normalizeIngredients = (ingredients) => {
          if (!ingredients || !Array.isArray(ingredients) || ingredients.length === 0) return null;
          return JSON.stringify([...ingredients].sort());
        };
        
        const itemVariationsKey = normalizeVariations(selectedVariations);
        const itemIngredientsKey = normalizeIngredients(selectedIngredients);
        const itemIdStr = String(itemId);
        const itemIndex = restaurantCart.items.findIndex((i) => {
          if (String(i._id) !== itemIdStr) return false;
          if (selectedVariations !== null || selectedIngredients !== null) {
            const existingVariationsKey = normalizeVariations(i.selectedVariations);
            const existingIngredientsKey = normalizeIngredients(i.selectedIngredients);
            return existingVariationsKey === itemVariationsKey && existingIngredientsKey === itemIngredientsKey;
          }
          return true;
        });
        
        if (itemIndex !== -1) {
          // Update the item quantity
          const updatedCart = [...prevCart];
          updatedCart[restaurantCartIndex] = {
            ...restaurantCart,
            items: [...restaurantCart.items]
          };
          updatedCart[restaurantCartIndex].items[itemIndex] = {
            ...updatedCart[restaurantCartIndex].items[itemIndex],
            quantity: quantity
          };
          return updatedCart;
        }
      }
      return prevCart;
    });
  };

  const clearCart = () => {
    setCart([]);
  };

  const removeRestaurantCart = (restaurantId) => {
    setCart((prevCart) => {
      return prevCart.filter((c) => c.restaurantId !== restaurantId);
    });
  };

  const updateItemVariations = (restaurantId, itemId, oldVariations, newVariations, oldIngredients = null, newIngredients = null) => {
    setCart((prevCart) => {
      const restaurantCartIndex = prevCart.findIndex((c) => String(c.restaurantId) === String(restaurantId));
      if (restaurantCartIndex !== -1) {
        const restaurantCart = prevCart[restaurantCartIndex];
        
        const normalizeVariations = (variations) => {
          if (!variations || typeof variations !== 'object' || Object.keys(variations).length === 0) return null;
          const sorted = Object.keys(variations).sort().reduce((acc, key) => {
            acc[key] = variations[key];
            return acc;
          }, {});
          return JSON.stringify(sorted);
        };
        const normalizeIngredients = (ingredients) => {
          if (!ingredients || !Array.isArray(ingredients) || ingredients.length === 0) return null;
          return JSON.stringify([...ingredients].sort());
        };
        
        const oldVariationsKey = normalizeVariations(oldVariations);
        const oldIngredientsKey = normalizeIngredients(oldIngredients);
        const itemIdStr = String(itemId);
        const itemIndex = restaurantCart.items.findIndex((i) => {
          if (String(i._id) !== itemIdStr) return false;
          const existingVarKey = normalizeVariations(i.selectedVariations);
          const matchVariations = oldVariationsKey == null
            ? (existingVarKey == null || existingVarKey === '{}')
            : existingVarKey === oldVariationsKey;
          const existingIngKey = normalizeIngredients(i.selectedIngredients);
          const matchIngredients = oldIngredientsKey == null
            ? (existingIngKey == null || existingIngKey === '[]')
            : existingIngKey === oldIngredientsKey;
          return matchVariations && matchIngredients;
        });
        
        if (itemIndex !== -1) {
          const updatedCart = [...prevCart];
          updatedCart[restaurantCartIndex] = {
            ...restaurantCart,
            items: [...restaurantCart.items]
          };
          const existingItem = updatedCart[restaurantCartIndex].items[itemIndex];
          const updatedItem = {
            ...existingItem,
            selectedVariations: newVariations && typeof newVariations === 'object' ? { ...newVariations } : newVariations
          };
          if (newIngredients !== undefined) {
            updatedItem.selectedIngredients = Array.isArray(newIngredients) ? [...newIngredients] : newIngredients;
          }
          updatedCart[restaurantCartIndex].items[itemIndex] = updatedItem;
          return updatedCart;
        }
      }
      return prevCart;
    });
  };

  const getCartTotal = () => {
    return cart.reduce((total, restaurantCart) => {
      const restaurantTotal = restaurantCart.items.reduce((sum, item) => {
        return sum + item.price * item.quantity;
      }, 0);
      return total + restaurantTotal;
    }, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, restaurantCart) => {
      return total + restaurantCart.items.reduce((sum, item) => sum + item.quantity, 0);
    }, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        removeRestaurantCart,
        getCartTotal,
        getCartItemCount,
        updateItemVariations
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
